<script lang="ts">
  import LineChart from '../charts/LineChart.svelte';
  import InfoTip from './InfoTip.svelte';
  import { navigate } from '../router.svelte';
  import { handle401, type LabelStat } from '../admin';
  import { renameLabel } from '@api';
  import { activeCommunity, community } from '../community.svelte';

  // Engagement-by-category table: sortable columns, a per-row volume-vs-engagement
  // trend (same two lines as the Overwhelm test), a per-category flood score, and a
  // click-through to that category's posts in the app.
  let { labels }: { labels: LabelStat[] } = $props();

  const CA = 'var(--color-primary)'; // posts
  const CB = 'var(--color-secondary)'; // engagement (comments/post)

  function pct(x: number | null | undefined): string {
    return x == null ? '—' : `${(x * 100).toFixed(x > 0 && x < 0.1 ? 1 : 0)}%`;
  }
  function num(x: number | null | undefined): string {
    return x == null ? '—' : x.toLocaleString();
  }
  function avg(x: number | null | undefined): string {
    return x == null ? '—' : x.toFixed(1);
  }
  const postsHref = (l: LabelStat): string => `/admin/community/timeline?label=${encodeURIComponent(l.label_id)}`;

  // Per-category flood verdict (same thresholds as the page-level Flooding Score).
  function flood(r: number | null): { text: string; cls: string } {
    if (r == null) return { text: '—', cls: 'text-base-content/50' };
    if (r <= -0.3) return { text: 'Overwhelmed', cls: 'text-error' };
    if (r >= 0.3) return { text: 'Hungry', cls: 'text-success' };
    return { text: 'Balanced', cls: 'text-base-content/65' };
  }
  const trend = (l: LabelStat) => [
    { name: 'posts', values: l.trend_posts as (number | null)[], color: CA },
    { name: 'engagement', values: l.trend_engagement, color: CB, dashed: true },
  ];

  // --- sorting ---
  type SortKey = 'name' | 'engagement_index' | 'posts' | 'flood_corr' | 'avg_upvotes' | 'avg_comments' | 'unanswered_rate';
  let sortKey = $state<SortKey>('engagement_index');
  let sortDir = $state<'asc' | 'desc'>('desc');
  function toggleSort(k: SortKey): void {
    if (sortKey === k) sortDir = sortDir === 'desc' ? 'asc' : 'desc';
    else {
      sortKey = k;
      sortDir = k === 'name' ? 'asc' : 'desc';
    }
  }
  const sorted = $derived.by(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const arr = [...labels];
    arr.sort((a, b) => {
      if (sortKey === 'name') {
        const an = (a.name ?? '~' + a.label_id).toLowerCase();
        const bn = (b.name ?? '~' + b.label_id).toLowerCase();
        return an < bn ? -dir : an > bn ? dir : 0;
      }
      const av = a[sortKey] as number | null;
      const bv = b[sortKey] as number | null;
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls always last
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
    return arr;
  });

  // --- inline label rename ---
  const skoolSlug = $derived(activeCommunity()?.slug ?? null);
  let editingId = $state<string | null>(null);
  let editValue = $state('');
  let savingId = $state<string | null>(null);
  let renameError = $state<string | null>(null);

  function startEdit(l: LabelStat): void {
    editingId = l.label_id;
    editValue = l.name ?? '';
    renameError = null;
  }
  function cancelEdit(): void {
    editingId = null;
    editValue = '';
  }
  /**
   * `PATCH /v1/communities/{id}/labels/{label_id}` — the label id is a path segment
   * now, where `POST /admin/labels/set-name` took `{skool_id, label_id, name}` in a
   * body. Both are to hand at the call site: `l.label_id` is the row we are editing
   * and the community is the panel's current selection, the same one the legacy body
   * carried.
   *
   * The response is THE LABEL, not `{status: "ok"}`, so the row is patched from what
   * the server stored rather than from what we hoped it stored (doc 02 W9). A blank
   * name is a 422 there and the guard above never sends one; a label id no post
   * carries is `404 label_not_found`, which is a real answer for a stale table rather
   * than a silently created orphan row.
   */
  async function saveEdit(l: LabelStat): Promise<void> {
    const id = community.selectedId;
    if (id == null) return;
    const name = editValue.trim();
    if (!name || name === (l.name ?? '')) {
      cancelEdit();
      return;
    }
    savingId = l.label_id;
    renameError = null;
    try {
      const saved = await renameLabel(id, l.label_id, name);
      const row = labels.find((x) => x.label_id === saved.label_id);
      if (row) row.name = saved.name;
      editingId = null;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      renameError = err instanceof Error ? err.message : 'Failed to save the label name.';
    } finally {
      savingId = null;
    }
  }
  function onEditKey(e: KeyboardEvent, l: LabelStat): void {
    if (e.key === 'Enter') void saveEdit(l);
    else if (e.key === 'Escape') cancelEdit();
  }
</script>

{#snippet sortTh(key: SortKey, label: string, extra: string, tip: string)}
  <th class="px-3 py-2.5 {extra}">
    <button
      class="eyebrow inline-flex items-center gap-1 whitespace-nowrap transition-colors hover:text-base-content/75 {sortKey === key ? 'text-base-content/75' : ''}"
      onclick={() => toggleSort(key)}
    >
      {label}
      <span class="text-[0.6rem] {sortKey === key ? 'opacity-80' : 'opacity-0'}">{sortDir === 'desc' ? '↓' : '↑'}</span>
    </button>
    {#if tip}<InfoTip class="ml-0.5" text={tip} />{/if}
  </th>
{/snippet}

{#if renameError}
  <p class="mb-2 text-xs text-error">{renameError}</p>
{/if}
{#if labels.length === 0}
  <p class="text-sm text-base-content/62">No labelled posts yet.</p>
{:else}
  <div class="overflow-x-auto rounded-box border hair bg-base-100">
    <table class="w-full min-w-[52rem] text-sm">
      <thead>
        <tr class="border-b hair bg-base-200/50 text-left">
          {@render sortTh('name', 'Label', '', '')}
          {@render sortTh('engagement_index', 'Index', 'text-right', 'Engagement per post (avg upvotes + comments) versus the whole community. ×1.0 = typical; ×2.0 = twice the usual pull; below ×1.0 = under-performs.')}
          {@render sortTh('posts', 'Posts', 'text-right', '')}
          <th class="px-3 py-2.5 text-center"><span class="eyebrow">Trend</span></th>
          {@render sortTh('flood_corr', 'Flood', 'text-left', 'Does posting more in this category help or hurt engagement? Correlation of weekly volume vs per-post engagement. Hungry = more posts, more engagement; Overwhelmed = it saturates.')}
          {@render sortTh('avg_upvotes', 'Avg ▲', 'text-right', '')}
          {@render sortTh('avg_comments', 'Avg 💬', 'text-right', '')}
          {@render sortTh('unanswered_rate', 'Unanswered', 'text-right', "Share of this category's posts that never got a single reply.")}
        </tr>
      </thead>
      <tbody>
        {#each sorted as l (l.label_id)}
          {@const f = flood(l.flood_corr)}
          <tr class="border-b hair transition-colors last:border-0 hover:bg-base-200/40">
            <td class="px-3 py-2">
              {#if editingId === l.label_id}
                <div class="flex items-center gap-1.5">
                  <!-- svelte-ignore a11y_autofocus -->
                  <input class="input input-bordered input-xs w-40 bg-base-100" bind:value={editValue} onkeydown={(e) => onEditKey(e, l)} placeholder="Label name" autofocus disabled={savingId === l.label_id} />
                  <button class="btn btn-xs btn-primary" onclick={() => saveEdit(l)} disabled={savingId === l.label_id}>{savingId === l.label_id ? 'Saving…' : 'Save'}</button>
                  <button class="btn btn-ghost btn-xs" onclick={cancelEdit} disabled={savingId === l.label_id}>Cancel</button>
                </div>
              {:else}
                <div class="group flex items-center gap-1.5">
                  {#if l.name}
                    <!-- privacy-ok: a topic-category label the classifier produced, not a person -->
                    <a class="font-medium hover:text-primary hover:underline" href={postsHref(l)} title="See this category's posts">{l.name}</a>
                  {:else}
                    <a class="font-mono text-xs text-base-content/65 hover:text-primary hover:underline" href={postsHref(l)} title="See this category's posts">#{l.label_id.slice(0, 8)}</a>
                    <span class="rounded bg-base-content/10 px-1.5 py-px font-mono text-[0.6rem] text-base-content/65">unnamed</span>
                    {#if skoolSlug}
                      <a class="inline-flex items-center gap-0.5 font-mono text-[0.6rem] text-primary/80 hover:text-primary hover:underline" href="https://www.skool.com/{skoolSlug}?c={l.label_id}" target="_blank" rel="noopener noreferrer" title="Open on Skool to identify it, then name it">
                        skool<svg class="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 17 17 7M8 7h9v9" /></svg>
                      </a>
                    {/if}
                  {/if}
                  <button class="text-base-content/54 opacity-0 transition-opacity hover:text-primary group-hover:opacity-100 focus:opacity-100" onclick={() => startEdit(l)} title="Rename label" aria-label="Rename label {l.name ?? l.label_id}">
                    <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" /></svg>
                  </button>
                </div>
              {/if}
            </td>
            <td class="px-3 py-2 text-right font-medium tabular-nums {l.engagement_index == null ? 'text-base-content/62' : l.engagement_index >= 1.2 ? 'text-success' : l.engagement_index <= 0.8 ? 'text-warning' : ''}">
              {l.engagement_index == null ? '—' : `×${l.engagement_index.toFixed(2)}`}
            </td>
            <td class="px-3 py-2 text-right tabular-nums">{num(l.posts)}</td>
            <td class="px-3 py-2">
              {#if l.trend_posts.length > 1}
                <div class="mx-auto w-24"><LineChart series={trend(l)} height={26} normalize legend={false} /></div>
              {:else}
                <span class="block text-center font-mono text-[0.6rem] text-base-content/50">·</span>
              {/if}
            </td>
            <td class="px-3 py-2">
              <span class="font-medium {f.cls}" title={l.flood_corr == null ? 'not enough weeks' : `flood r = ${l.flood_corr.toFixed(2)}`}>{f.text}</span>
            </td>
            <td class="px-3 py-2 text-right tabular-nums">{avg(l.avg_upvotes)}</td>
            <td class="px-3 py-2 text-right tabular-nums">{avg(l.avg_comments)}</td>
            <td class="px-3 py-2 text-right tabular-nums {(l.unanswered_rate ?? 0) > 0.3 ? 'text-warning' : 'text-base-content/72'}">
              {#if (l.unanswered_rate ?? 0) > 0}
                <a class="inline-flex items-center gap-0.5 hover:underline" href="/admin/community/timeline?label={l.label_id}&unanswered=1" target="_blank" rel="noopener noreferrer" title="Open this category's unanswered posts">
                  {pct(l.unanswered_rate)}<svg class="h-2.5 w-2.5 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 17 17 7M8 7h9v9" /></svg>
                </a>
              {:else}
                {pct(l.unanswered_rate)}
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}
